import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { privateAxios } from "../axiosInstances";
import {
  CancelCommercialisationTaskMeeting,
  CommercialisationTaskMeetingForm,
  CreateCommercialisationOrganiserEventForm,
  CreateCommercialisationCommentForm,
  CreateCommercialisationTaskForm,
  CreateCommercialisationTaskInterlocutorForm,
  UpdateCommercialisationOrganiserEventForm,
  UpdateCommercialisationTaskAssigneeForm,
  UpdateCommercialisationTaskDeadlineForm,
  UpdateCommercialisationTaskEventForm,
  UpdateCommercialisationTaskInterlocutorForm,
  UpdateCommercialisationTaskOrganiserForm,
  UpdateCommercialisationTaskStatusForm,
  UpdateCommercialisationTaskTargetForm,
  UpdateCommercialisationTaskTargetProvenanceForm,
  UpdateCommercialisationTaskTemperatureForm,
  CommercialisationBasketMeetingForm,
  CancelCommercialisationBasketMeeting,
  UpdateCommercialisationTaskEventStatisticsForm,
} from "../schemas/commercialisation/upsert-task.schema";
import {
  basketQueryKeys,
  commercialisationTasksQueryKeys,
} from "../query-keys";
import {
  CommercialisationMeeting,
  CommercialisationTask,
  GetBasketResume,
} from "@ac-expo/api-types";
import { EmailGenerationForm } from "../schemas";
import { toDatabaseFormat } from "@ac-expo/utils";

export const createCommercialisationTask = async (
  body: CreateCommercialisationTaskForm
) => {
  try {
    const { data } = await privateAxios.post<CommercialisationTask>("/tasks", {
      ...body,
      date: toDatabaseFormat(body.date),
      exhibitorCount: 0,
      animationCount: 0,
      ticketingAmount: 0,
      visitorCount: 0,
    });
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message: error.response?.data.message,
      };
    }
    throw error;
  }
};

export const useCreateCommercialisationTaskMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createCommercialisationTask,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: commercialisationTasksQueryKeys.all,
      });
    },
  });
};

export const updateCommercialisationTaskTarget = async (
  taskId: string,
  body: UpdateCommercialisationTaskTargetForm
) => {
  try {
    const { data } = await privateAxios.patch<CommercialisationTask>(
      `/tasks/${taskId}/target`,
      body
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw { ...error, message: error.response?.data.message };
    }
    throw error;
  }
};

export const useUpdateCommercialisationTaskTargetMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      taskId,
      body,
    }: {
      taskId: string;
      body: UpdateCommercialisationTaskTargetForm;
    }) => updateCommercialisationTaskTarget(taskId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: commercialisationTasksQueryKeys.all,
      });
    },
  });
};

export const updateCommercialisationTaskStatus = async (
  id: string,
  body: UpdateCommercialisationTaskStatusForm
) => {
  try {
    const { data } = await privateAxios.patch<CommercialisationTask>(
      `/tasks/${id}/status`,
      body
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw { ...error, message: error.response?.data.message };
    }
    throw error;
  }
};

export const useUpdateCommercialisationTaskStatusMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: UpdateCommercialisationTaskStatusForm;
    }) => updateCommercialisationTaskStatus(id, body),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: commercialisationTasksQueryKeys.byId(variables.id),
      });
    },
  });
};

export const updateCommercialisationTaskAssignee = async (
  id: string,
  body: UpdateCommercialisationTaskAssigneeForm
) => {
  try {
    const { data } = await privateAxios.patch<CommercialisationTask>(
      `/tasks/${id}/assignee`,
      body
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw { ...error, message: error.response?.data.message };
    }
    throw error;
  }
};

export const useUpdateCommercialisationTaskAssigneeMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: UpdateCommercialisationTaskAssigneeForm;
    }) => updateCommercialisationTaskAssignee(id, body),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: commercialisationTasksQueryKeys.byId(variables.id),
      });
    },
  });
};

export const updateCommercialisationTaskOrganiser = async (
  id: string,
  body: UpdateCommercialisationTaskOrganiserForm
) => {
  try {
    const { data } = await privateAxios.patch<CommercialisationTask>(
      `/tasks/${id}/organiser`,
      body
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw { ...error, message: error.response?.data.message };
    }
    throw error;
  }
};

export const useUpdateCommercialisationTaskOrganiserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: UpdateCommercialisationTaskOrganiserForm;
    }) => updateCommercialisationTaskOrganiser(id, body),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: commercialisationTasksQueryKeys.byId(variables.id),
      });
    },
  });
};

export const createCommercialisationTaskComment = async (
  taskId: string,
  body: CreateCommercialisationCommentForm
) => {
  try {
    const { data } =
      await privateAxios.post<CreateCommercialisationCommentForm>(
        `/tasks/${taskId}/comment`,
        body
      );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw { ...error, message: error.response?.data.message };
    }
    throw error;
  }
};

export const useCreateCommercialisationTaskCommentMutation = (
  taskId: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CreateCommercialisationCommentForm) =>
      createCommercialisationTaskComment(taskId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: commercialisationTasksQueryKeys.byId(taskId),
      });
    },
  });
};

export const createCommercialisationBasketComment = async (
  basketId: string,
  body: CreateCommercialisationCommentForm
) => {
  try {
    const { data } =
      await privateAxios.post<CreateCommercialisationCommentForm>(
        `/basket/${basketId}/comment`,
        body
      );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw { ...error, message: error.response?.data.message };
    }
    throw error;
  }
};

export const useCreateCommercialisationBasketCommentMutation = (
  basketId: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CreateCommercialisationCommentForm) =>
      createCommercialisationBasketComment(basketId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: basketQueryKeys.getById(basketId),
      });
    },
  });
};

export const createCommercialisationTaskMeeting = async (
  body: CommercialisationTaskMeetingForm
) => {
  try {
    const { data } = await privateAxios.post<CommercialisationMeeting>(
      `/tasks/${body.taskId}/meeting`,
      body
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw { ...error, message: error.response?.data.message };
    }
    throw error;
  }
};
export const useCreateCommercialisationTaskMeeting = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createCommercialisationTaskMeeting,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: commercialisationTasksQueryKeys.byId(variables.taskId),
      });
    },
  });
};

export const updateCommercialisationTaskMeeting = async (
  body: CommercialisationTaskMeetingForm
) => {
  try {
    const { data } = await privateAxios.patch<CommercialisationTask>(
      `/tasks/${body.taskId}/meeting/${body.meetingId}`,
      body
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw { ...error, message: error.response?.data.message };
    }
    throw error;
  }
};

export const useUpdateCommercialisationTaskMeeting = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateCommercialisationTaskMeeting,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: commercialisationTasksQueryKeys.byId(variables.taskId),
      });
    },
  });
};

const cancelCommercialisationTaskMeeting = async (
  body: CancelCommercialisationTaskMeeting
) => {
  try {
    const { data } = await privateAxios.delete<CommercialisationTask>(
      `/tasks/${body.taskId}/meeting/${body.meetingId}`
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw { ...error, message: error.response?.data.message };
    }
    throw error;
  }
};

export const useCancelCommercialisationTaskMeeting = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: cancelCommercialisationTaskMeeting,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: commercialisationTasksQueryKeys.all,
      });
    },
  });
};

export const createCommercialisationBasketMeeting = async (
  body: CommercialisationBasketMeetingForm
) => {
  try {
    const { data } = await privateAxios.post<CommercialisationMeeting>(
      `/basket/${body.basketId}/meeting`,
      body
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw { ...error, message: error.response?.data.message };
    }
    throw error;
  }
};
export const useCreateCommercialisationBasketMeeting = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createCommercialisationBasketMeeting,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: basketQueryKeys.getById(variables.basketId),
      });
    },
  });
};

export const updateCommercialisationBasketMeeting = async (
  body: CommercialisationBasketMeetingForm
) => {
  try {
    const { data } = await privateAxios.patch<GetBasketResume>(
      `/basket/${body.basketId}/meeting/${body.meetingId}`,
      body
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw { ...error, message: error.response?.data.message };
    }
    throw error;
  }
};

export const useUpdateCommercialisationBasketMeeting = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateCommercialisationBasketMeeting,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: basketQueryKeys.getById(variables.basketId),
      });
    },
  });
};

const cancelCommercialisationBasketMeeting = async (
  body: CancelCommercialisationBasketMeeting
) => {
  try {
    const { data } = await privateAxios.delete<GetBasketResume>(
      `/basket/${body.basketId}/meeting/${body.meetingId}`
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw { ...error, message: error.response?.data.message };
    }
    throw error;
  }
};

export const useCancelCommercialisationBasketMeeting = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: cancelCommercialisationBasketMeeting,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: basketQueryKeys.all,
      });
    },
  });
};

export const updateCommercialisationTaskEvent = async (
  id: string,
  body: UpdateCommercialisationTaskEventForm
) => {
  try {
    const { data } = await privateAxios.patch<CommercialisationTask>(
      `/tasks/${id}/event`,
      body
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw { ...error, message: error.response?.data.message };
    }
    throw error;
  }
};

export const useUpdateCommercialisationTaskEventMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: UpdateCommercialisationTaskEventForm;
    }) => updateCommercialisationTaskEvent(id, body),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: commercialisationTasksQueryKeys.byId(variables.id),
      });
    },
  });
};

export const updateCommercialisationTaskTemperature = async (
  id: string,
  body: UpdateCommercialisationTaskTemperatureForm
) => {
  try {
    const { data } = await privateAxios.patch<CommercialisationTask>(
      `/tasks/${id}/temperature`,
      body
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw { ...error, message: error.response?.data.message };
    }
    throw error;
  }
};

export const useUpdateCommercialisationTaskTemperatureMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: UpdateCommercialisationTaskTemperatureForm;
    }) => updateCommercialisationTaskTemperature(id, body),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: commercialisationTasksQueryKeys.byId(variables.id),
      });
    },
  });
};

export const updateCommercialisationTaskTargetProvenance = async (
  id: string,
  body: UpdateCommercialisationTaskTargetProvenanceForm
) => {
  try {
    const { data } = await privateAxios.patch<CommercialisationTask>(
      `/tasks/${id}/provenance`,
      body
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw { ...error, message: error.response?.data.message };
    }
    throw error;
  }
};

export const useUpdateCommercialisationTaskTargetProvenanceMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: UpdateCommercialisationTaskTargetProvenanceForm;
    }) => updateCommercialisationTaskTargetProvenance(id, body),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: commercialisationTasksQueryKeys.byId(variables.id),
      });
    },
  });
};

export const updateCommercialisationTaskDeadline = async (
  taskId: string,
  body: UpdateCommercialisationTaskDeadlineForm
) => {
  try {
    const { data } = await privateAxios.patch<CommercialisationTask>(
      `/tasks/${taskId}/deadline`,
      {
        ...body,
        date: toDatabaseFormat(body.date),
      }
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw { ...error, message: error.response?.data.message };
    }
    throw error;
  }
};

export const useUpdateCommercialisationTaskDeadlineMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      taskId,
      body,
    }: {
      taskId: string;
      body: UpdateCommercialisationTaskDeadlineForm;
    }) => updateCommercialisationTaskDeadline(taskId, body),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: commercialisationTasksQueryKeys.byId(variables.taskId),
      });
    },
  });
};

export const createCommercialisationOrganiserEvent = async (
  taskId: string,
  body: CreateCommercialisationOrganiserEventForm
) => {
  try {
    const { data } = await privateAxios.post(`/tasks/${taskId}/event`, body);
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw { ...error, message: error.response?.data.message };
    }
    throw error;
  }
};

export const useCreateCommercialisationOrganiserEventMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      taskId,
      body,
    }: {
      taskId: string;
      body: CreateCommercialisationOrganiserEventForm;
    }) => createCommercialisationOrganiserEvent(taskId, body),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: commercialisationTasksQueryKeys.byId(variables.taskId),
      });
    },
  });
};

export const updateCommercialisationOrganiserEvent = async (
  taskId: string,
  eventId: string,
  body: UpdateCommercialisationOrganiserEventForm
) => {
  try {
    const { data } = await privateAxios.patch<CommercialisationTask>(
      `/tasks/${taskId}/event/${eventId}`,
      body
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw { ...error, message: error.response?.data.message };
    }
    throw error;
  }
};

export const useUpdateCommercialisationOrganiserEventMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      taskId,
      eventId,
      body,
    }: {
      taskId: string;
      eventId: string;
      body: UpdateCommercialisationOrganiserEventForm;
    }) => updateCommercialisationOrganiserEvent(taskId, eventId, body),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: commercialisationTasksQueryKeys.byId(variables.taskId),
      });
    },
  });
};

export const updateCommercialisationTaskEventStatistics = async (
  taskId: string,
  body: UpdateCommercialisationTaskEventStatisticsForm
) => {
  try {
    const { data } = await privateAxios.patch<CommercialisationTask>(
      `/tasks/${taskId}/stats`,
      body
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw { ...error, message: error.response?.data.message };
    }
    throw error;
  }
};

export const useUpdateCommercialisationTaskEventStatisticsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      taskId,
      body,
    }: {
      taskId: string;
      body: UpdateCommercialisationTaskEventStatisticsForm;
    }) => updateCommercialisationTaskEventStatistics(taskId, body),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: commercialisationTasksQueryKeys.byId(variables.taskId),
      });
    },
  });
};

export const updateCommercialisationOrganiserEventIsArchived = async (
  taskId: string,
  eventId: string,
  isArchived: boolean
) => {
  try {
    const { data } = await privateAxios.patch<CommercialisationTask>(
      `/tasks/${taskId}/event/${eventId}/isArchived`,
      { isArchived }
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw { ...error, message: error.response?.data.message };
    }
    throw error;
  }
};

export const useUpdateCommercialisationOrganiserEventIsArchivedMutation =
  () => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: ({
        taskId,
        eventId,
        isArchived,
      }: {
        taskId: string;
        eventId: string;
        isArchived: boolean;
      }) =>
        updateCommercialisationOrganiserEventIsArchived(
          taskId,
          eventId,
          isArchived
        ),
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({
          queryKey: commercialisationTasksQueryKeys.byId(variables.taskId),
        });
      },
    });
  };

export const deleteCommercialisationOrganiserEvent = async (
  taskId: string,
  eventId: string
) => {
  try {
    const { data } = await privateAxios.delete<CommercialisationTask>(
      `/tasks/${taskId}/event/${eventId}`
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw { ...error, message: error.response?.data.message };
    }
    throw error;
  }
};

export const useDeleteCommercialisationOrganiserEventMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ taskId, eventId }: { taskId: string; eventId: string }) =>
      deleteCommercialisationOrganiserEvent(taskId, eventId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: commercialisationTasksQueryKeys.all,
      });
    },
  });
};

export const createCommercialisationTaskInterlocutor = async (
  taskId: string,
  body: CreateCommercialisationTaskInterlocutorForm
) => {
  try {
    const { data } = await privateAxios.post(
      `/tasks/${taskId}/interlocutor`,
      body
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw { ...error, message: error.response?.data.message };
    }
    throw error;
  }
};

export const useCreateCommercialisationTaskInterlocutorMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      taskId,
      body,
    }: {
      taskId: string;
      body: CreateCommercialisationTaskInterlocutorForm;
    }) => createCommercialisationTaskInterlocutor(taskId, body),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: commercialisationTasksQueryKeys.byId(variables.taskId),
      });
    },
  });
};

export const updateCommercialisationTaskInterlocutor = async (
  taskId: string,
  interlocutorId: string,
  body: UpdateCommercialisationTaskInterlocutorForm
) => {
  try {
    const { data } = await privateAxios.patch<CommercialisationTask>(
      `/tasks/${taskId}/interlocutor/${interlocutorId}`,
      body
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw { ...error, message: error.response?.data.message };
    }
    throw error;
  }
};

export const useUpdateCommercialisationTaskInterlocutorMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      taskId,
      interlocutorId,
      body,
    }: {
      taskId: string;
      interlocutorId: string;
      body: UpdateCommercialisationTaskInterlocutorForm;
    }) => updateCommercialisationTaskInterlocutor(taskId, interlocutorId, body),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: commercialisationTasksQueryKeys.byId(variables.taskId),
      });
    },
  });
};

export const updateCommercialisationTaskInterlocutorIsArchived = async (
  taskId: string,
  interlocutorId: string,
  isArchived: boolean
) => {
  try {
    const { data } = await privateAxios.patch<CommercialisationTask>(
      `/tasks/${taskId}/interlocutor/${interlocutorId}/isArchived`,
      { isArchived }
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw { ...error, message: error.response?.data.message };
    }
    throw error;
  }
};

export const useUpdateCommercialisationTaskInterlocutorIsArchivedMutation =
  () => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: ({
        taskId,
        interlocutorId,
        isArchived,
      }: {
        taskId: string;
        interlocutorId: string;
        isArchived: boolean;
      }) =>
        updateCommercialisationTaskInterlocutorIsArchived(
          taskId,
          interlocutorId,
          isArchived
        ),
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({
          queryKey: commercialisationTasksQueryKeys.byId(variables.taskId),
        });
      },
    });
  };

export const deleteCommercialisationTaskInterlocutor = async (
  taskId: string,
  interlocutorId: string
) => {
  try {
    const { data } = await privateAxios.delete<CommercialisationTask>(
      `/tasks/${taskId}/interlocutor/${interlocutorId}`
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw { ...error, message: error.response?.data.message };
    }
    throw error;
  }
};

export const useDeleteCommercialisationTaskInterlocutorMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      taskId,
      interlocutorId,
    }: {
      taskId: string;
      interlocutorId: string;
    }) => deleteCommercialisationTaskInterlocutor(taskId, interlocutorId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: commercialisationTasksQueryKeys.byId(variables.taskId),
      });
    },
  });
};

export const sendCommercialisationMail = async (
  taskId: string,
  mailData: EmailGenerationForm
) => {
  try {
    const { data } = await privateAxios.post(`/tasks/${taskId}/mail`, {
      subject: mailData.mailObject,
      emailAdresses: [mailData.mailTarget],
      htmlContent: mailData.mailContent,
      source: "contact@connect-events.fr",
      senderName: "Brian BESANCENOT",
      previewText: mailData.mailPreview,
    });
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw { ...error, message: error.response?.data.message };
    }
    throw error;
  }
};

export const useSendCommercialisationMailMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      taskId,
      mailData,
    }: {
      taskId: string;
      mailData: EmailGenerationForm;
    }) => sendCommercialisationMail(taskId, mailData),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: commercialisationTasksQueryKeys.byId(variables.taskId),
      });
    },
  });
};
